// src/components/SearchResults.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Download, Filter, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { performSearch } from '../services/api';
import ResultCard, { getIconForPlatform, getCategoryFromResult } from './ResultCard';
import { generatePDFReport } from '../utils/pdfGenerator';

const TABS = [
  { key: 'all',     label: 'All',       icon: Filter },
  { key: 'found',   label: 'Found',     icon: CheckCircle },
  { key: 'missing', label: 'Not Found', icon: XCircle },
  { key: 'error',   label: 'Error',     icon: AlertCircle },
];

const SearchResults = ({ searchTerm, searchType, onNewSearch }) => {
  const [results, setResults] = useState({});
  const [stats, setStats] = useState({ found: 0, notFound: 0, error: 0, total: 0 });
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [disclaimer, setDisclaimer] = useState(null);
  const [scanDurationMs, setScanDurationMs] = useState(null);

  const progressTimerRef = useRef(null);
  const displayTerm = searchTerm instanceof File ? searchTerm.name : searchTerm;

  // Simulated incremental progress while the backend works
  const simulateProgress = useCallback(() => {
    setProgress(5);
    let p = 5;
    progressTimerRef.current = setInterval(() => {
      p += Math.random() * 8;
      if (p >= 90) {
        clearInterval(progressTimerRef.current);
        p = 90;
      }
      setProgress(Math.min(p, 90));
    }, 600);
  }, []);

  const executeSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    setActiveTab('all');
    simulateProgress();

    try {
      const response = await performSearch(searchType, searchTerm);
      clearInterval(progressTimerRef.current);
      setProgress(100);

      const platformResults = response.results || {};
      setResults(platformResults);
      setScanDurationMs(response.scan_duration_ms ?? null);
      if (response.email_disclaimer) setDisclaimer(response.email_disclaimer);

      let found = 0, notFound = 0, errCount = 0;
      Object.values(platformResults).forEach((r) => {
        if (r?.error) errCount++;
        else if (r?.found) found++;
        else notFound++;
      });
      setStats({ found, notFound, error: errCount, total: Object.keys(platformResults).length });

    } catch (err) {
      clearInterval(progressTimerRef.current);
      setProgress(0);
      setError(err.message || 'Search failed. Make sure the backend server is running.');
      setStats({ found: 0, notFound: 0, error: 0, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [searchType, searchTerm, simulateProgress]);

  useEffect(() => {
    executeSearch();
    return () => clearInterval(progressTimerRef.current);
  }, [executeSearch]);

  // Build tool list
  const allTools = Object.entries(results).map(([name, result]) => ({
    name,
    icon: getIconForPlatform(name),
    category: getCategoryFromResult(name),
    result,
  }));

  // Filter by active tab
  const filteredTools = allTools.filter((t) => {
    if (activeTab === 'all')     return true;
    if (activeTab === 'found')   return t.result?.found && !t.result?.error;
    if (activeTab === 'missing') return !t.result?.found && !t.result?.error;
    if (activeTab === 'error')   return !!t.result?.error;
    return true;
  });

  const handleDownloadPDF = () => {
    generatePDFReport({ searchType, searchTerm, stats, tools: allTools });
  };

  const formatDuration = (ms) => {
    if (ms == null) return null;
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <div className="animate-fade-in" style={{ width: '100%', maxWidth: 960 }}>
      <div className="glass" style={{
        borderRadius: 'var(--radius-xl)',
        maxHeight: '92vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* ── Header ── */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--color-border)',
          background: 'rgba(0,0,0,0.15)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Search Results</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: 12,
                  background: 'rgba(0,0,0,0.35)', padding: '3px 10px',
                  borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)',
                }}>
                  {displayTerm}
                </span>
                <span className="badge badge-accent">{searchType}</span>
                {scanDurationMs != null && !loading && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--color-text-muted)', fontSize: 11 }}>
                    <Clock size={11} />
                    {formatDuration(scanDurationMs)}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                onClick={handleDownloadPDF}
                disabled={loading || allTools.length === 0}
                className="btn btn-ghost btn-sm"
              >
                <Download size={13} /> Report
              </button>
              <button onClick={onNewSearch} className="btn btn-primary btn-sm">
                <Search size={13} /> New Search
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 6 }}>
              <span>
                {loading
                  ? '⏳ Scanning platforms…'
                  : error
                  ? '❌ Scan failed'
                  : `✅ Scan complete — ${stats.total} platforms checked`}
              </span>
              <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                {Math.round(progress)}%
              </span>
            </div>
            <div className="progress-track">
              <div
                className="progress-fill"
                style={{ width: `${progress}%`, background: error ? 'var(--color-rose)' : undefined }}
              />
            </div>
          </div>

          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
            {[
              { label: 'Found',     value: stats.found,    bg: 'rgba(16,185,129,0.12)', color: '#6ee7b7', border: 'rgba(16,185,129,0.22)' },
              { label: 'Not Found', value: stats.notFound, bg: 'rgba(244,63,94,0.10)',  color: '#fda4af', border: 'rgba(244,63,94,0.20)'  },
              { label: 'Error',     value: stats.error,    bg: 'rgba(245,158,11,0.10)', color: '#fcd34d', border: 'rgba(245,158,11,0.20)' },
            ].map(({ label, value, bg, color, border }) => (
              <div key={label} style={{
                background: bg, borderRadius: 'var(--radius-md)',
                border: `1px solid ${border}`, padding: '10px 14px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Email disclaimer ── */}
        {disclaimer && !loading && (
          <div style={{
            padding: '10px 24px',
            background: 'rgba(99,102,241,0.07)',
            borderBottom: '1px solid rgba(99,102,241,0.15)',
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}>
            <AlertCircle size={14} color="#818cf8" style={{ flexShrink: 0, marginTop: 2 }} />
            <p style={{ margin: 0, fontSize: 11, color: '#a5b4fc', lineHeight: 1.5 }}>{disclaimer}</p>
          </div>
        )}

        {/* ── Error message ── */}
        {error && (
          <div style={{
            padding: '12px 24px',
            background: 'rgba(244,63,94,0.10)',
            borderBottom: '1px solid rgba(244,63,94,0.20)',
          }}>
            <p style={{ margin: 0, fontSize: 13, color: '#fda4af' }}>{error}</p>
          </div>
        )}

        {/* ── Filter tabs ── */}
        {!loading && allTools.length > 0 && (
          <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--color-border)' }}>
            <div className="tab-bar">
              {TABS.map(({ key, label }) => {
                const count = key === 'all' ? stats.total
                  : key === 'found'   ? stats.found
                  : key === 'missing' ? stats.notFound
                  : stats.error;
                return (
                  <button
                    key={key}
                    className={`tab-item${activeTab === key ? ' active' : ''}`}
                    onClick={() => setActiveTab(key)}
                  >
                    {label}
                    {count > 0 && (
                      <span style={{ marginLeft: 5, opacity: 0.7, fontWeight: 400 }}>
                        ({count})
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Results list ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loading ? (
            // Skeleton cards
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="shimmer" style={{ height: 68, borderRadius: 'var(--radius-md)' }} />
            ))
          ) : filteredTools.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 24px', color: 'var(--color-text-muted)' }}>
              {allTools.length === 0 ? 'No results returned.' : `No ${activeTab} results.`}
            </div>
          ) : (
            filteredTools.map((tool, index) => (
              <ResultCard
                key={tool.name}
                tool={tool}
                result={tool.result}
                index={index}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export { getIconForPlatform, getCategoryFromResult };
export default SearchResults;
